import axios from 'axios';

export type CustomerPhoto = {
  _id: string;
  _business?: string;
  photoUrl: string | null;
};

type PendingPhoto = {
  id: string;
  revision: number;
  resolve: (customer: CustomerPhoto) => void;
  reject: (error: unknown) => void;
};

// Avatars mount together in lists. Combine their reads so older services can
// keep their existing customer projections without making one request per row.
const pending = new Map<string, PendingPhoto[]>();
let scheduled = false;
let revision = 0;
const confirmed = new Map<string, { revision: number; customer: CustomerPhoto }>();

export function recordSavedCustomerPhoto(customer: CustomerPhoto) {
  confirmed.set(`${customer._business || ''}:${customer._id}`, { revision: ++revision, customer });
}

function newerSavedPhoto(business: string, entry: PendingPhoto) {
  const saved = confirmed.get(`${business}:${entry.id}`);
  return saved && saved.revision > entry.revision ? saved.customer : undefined;
}

async function flushPhotos() {
  const batches = [...pending.entries()];
  pending.clear();
  scheduled = false;
  await Promise.all(batches.map(async ([business, entries]) => {
    const ids = [...new Set(entries.map((entry) => entry.id))];
    for (let offset = 0; offset < ids.length; offset += 100) {
      const customerIds = ids.slice(offset, offset + 100);
      const requested = entries.filter((entry) => customerIds.includes(entry.id));
      try {
        const { data } = await axios.post('/api/customer-photos', {
          customerIds,
          ...(business ? { _business: business } : {}),
        }, { withCredentials: true, timeout: 15000 });
        for (const entry of requested) {
          // A read may start before an upload commits and finish afterwards.
          // Never let that older response replace the confirmed saved image.
          const customer = newerSavedPhoto(business, entry) ||
            data.customers?.find((item: CustomerPhoto) => item._id === entry.id);
          if (customer) entry.resolve(customer);
          else entry.reject(new Error('Customer photo is unavailable.'));
        }
      } catch (error) {
        for (const entry of requested) {
          const saved = newerSavedPhoto(business, entry);
          if (saved) entry.resolve(saved);
          else entry.reject(error);
        }
      }
    }
  }));
}

export function requestCustomerPhoto(_customer: string, _business?: string): Promise<CustomerPhoto> {
  return new Promise((resolve, reject) => {
    const business = _business || '';
    const entries = pending.get(business) || [];
    entries.push({ id: _customer, revision, resolve, reject });
    pending.set(business, entries);
    if (!scheduled) {
      scheduled = true;
      setTimeout(flushPhotos, 0);
    }
  });
}

export function customerPhotoRequestError(error: any) {
  return {
    status: error.response?.status || 'CUSTOM_ERROR',
    data: {
      message: error.response?.data?.message ||
        (typeof error.response?.data?.error === 'string' ? error.response.data.error : undefined) ||
        'The photo could not be saved. Please try again.',
    },
  };
}
